import axios from "axios";
export const AUTH_TOKEN_STORAGE_KEY = "austuition.access_token";

const configuredBackendPort = (process.env.REACT_APP_BACKEND_PORT || "8001").trim();
const configuredBackendFallbackPort = (
  process.env.REACT_APP_BACKEND_FALLBACK_PORT || "8000"
).trim();
const DEFAULT_LOCAL_BACKEND_ORIGINS = [
  `http://localhost:${configuredBackendPort}`,
  `http://localhost:${configuredBackendFallbackPort}`,
];
const DEFAULT_TUNNEL_BACKEND_ORIGIN =
  "https://cheque-maryland-park-centers.trycloudflare.com";

const LOCAL_HOST_PATTERNS = [
  /^localhost$/i,
  /^127(?:\.\d{1,3}){3}$/i,
  /^0\.0\.0\.0$/i,
  /^::1$/i,
  /^192\.168\./i,
  /^10\./i,
  /^172\.(1[6-9]|2\d|3[0-1])\./i,
];

const isLikelyLocalHost = (host) =>
  LOCAL_HOST_PATTERNS.some((pattern) => pattern.test(host || ""));

const inferLocalBackendOrigins = () => {
  if (typeof window === "undefined") return DEFAULT_LOCAL_BACKEND_ORIGINS;
  const host = window.location.hostname || "localhost";
  const protocol = window.location.protocol === "https:" ? "https" : "http";

  if (/\.trycloudflare\.com$/i.test(host)) {
    return [DEFAULT_TUNNEL_BACKEND_ORIGIN];
  }

  if (isLikelyLocalHost(host)) {
    const candidates = [
      `${protocol}://${host}:${configuredBackendPort}`,
      `${protocol}://${host}:${configuredBackendFallbackPort}`,
      // On local/dev, backend commonly serves plain HTTP even when frontend runs on HTTPS.
      // Keep explicit HTTP fallbacks to avoid ERR_NETWORK from TLS/protocol mismatches.
      `http://${host}:${configuredBackendPort}`,
      `http://${host}:${configuredBackendFallbackPort}`,
      ...DEFAULT_LOCAL_BACKEND_ORIGINS,
    ];
    return candidates;
  }

  // Production domains serve /api via nginx on 443 — do not append backend port.
  return [`${protocol}://${host}`];
};

const normalizeBackendOrigin = (value) => {
  const trimmed = (value || "").trim();
  if (!trimmed) return "";
  const withoutTrailingSlash = trimmed.replace(/\/+$/, "");
  return withoutTrailingSlash.endsWith("/api")
    ? withoutTrailingSlash.slice(0, -4)
    : withoutTrailingSlash;
};

const dedupe = (values) => {
  const seen = new Set();
  return values.filter((value) => {
    if (!value || seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};

const isLikelyLocalOrigin = (origin) => {
  try {
    const parsed = new URL(origin);
    return isLikelyLocalHost(parsed.hostname);
  } catch (_) {
    return false;
  }
};

const buildBackendOrigins = (preferredValue) => {
  const preferredOrigin = normalizeBackendOrigin(preferredValue);
  const inferredOrigins = inferLocalBackendOrigins().map(normalizeBackendOrigin);

  if (!preferredOrigin) return dedupe(inferredOrigins);
  if (!isLikelyLocalOrigin(preferredOrigin)) return [preferredOrigin];
  return dedupe([preferredOrigin, ...inferredOrigins]);
};

const backendOrigins = buildBackendOrigins(process.env.REACT_APP_BACKEND_URL);
let backendOriginIndex = 0;

const currentApiBase = () => {
  const index = Math.min(backendOriginIndex, backendOrigins.length - 1);
  return `${backendOrigins[index]}/api`;
};

const switchToNextBackendOrigin = () => {
  if (backendOriginIndex + 1 >= backendOrigins.length) return false;
  backendOriginIndex += 1;
  api.defaults.baseURL = currentApiBase();
  return true;
};

export const API = currentApiBase();

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  if (config?.skipAuth) return config;
  try {
    const token = window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (token) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch (_) {
    // ignore storage access issues
  }
  return config;
});

// Auto-refresh once on a 401 to recover from short token expiries.
let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err?.config || {};
    const status = err?.response?.status;
    const url = cfg.url || "";
    const isNetworkError =
      !err?.response &&
      (err?.code === "ERR_NETWORK" || /network error/i.test(err?.message || ""));

    // In local dev, recover when frontend points to 8001 but backend is on 8000 (or vice-versa).
    if (isNetworkError && !cfg.__backendFailoverRetried && switchToNextBackendOrigin()) {
      cfg.__backendFailoverRetried = true;
      cfg.baseURL = api.defaults.baseURL;
      try {
        return await api(cfg);
      } catch (_retryErr) {
        // Failover also failed — revert so future requests still try the primary port.
        backendOriginIndex -= 1;
        api.defaults.baseURL = currentApiBase();
        return Promise.reject(_retryErr);
      }
    }

    // Don't try to refresh on auth endpoints themselves.
    if (status === 401 && !cfg.__retried && !url.includes("/auth/")) {
      cfg.__retried = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        const refreshResp = await refreshing;
        const token = refreshResp?.data?.access_token;
        if (token) {
          try {
            window.localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
          } catch (_) {
            // ignore storage failures
          }
        }
        refreshing = null;
        return api(cfg);
      } catch (_) {
        refreshing = null;
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
